import { useEffect, useState } from 'react';
import { FULL_HEIGHT_ATTRIBUTE, TRANSITION_ATTRIBUTE, setSheetStateAttribute } from '../sheetStateAttributes';
import type { SheetEngineProps } from '../sheetTypes';
import {
    getSheetHeightAdjustedForClose,
    setContentHeightToMax,
    setContentHeightToVisibleSpace,
    setSheetHeight,
    setSheetHeightVariable,
} from '../utils/sheetHeightUtils';
import { initialiseSnapPoints } from '../utils/snapPointUtils';
import runOnTransitionEndWithTimeout from '../utils/runOnTransitionEndWithTimeout';

type SheetSnapPointState = {
    snapPointsState: number[];
    addSnapPoint: (snapPoint: number) => void;
};

/**
 * Take care of snapPoint and activeSnapPoint state, and expose functions to update it
 */
const useSheetSnapPointState = (sheetProps: SheetEngineProps): SheetSnapPointState => {
    const {
        sheetContainerRef,
        sheetRef,
        snapPoints = [],
        activeSnapPoint,
        preventDragDismiss = false,
        useViewportResizeForKeyboard = false,
        topInset = 0,
    } = sheetProps;

    const [snapPointsState, setSnapPointsState] = useState<number[]>(
        initialiseSnapPoints(snapPoints, preventDragDismiss),
    );

    const addSnapPoint = (snapPoint: number) => {
        if (snapPointsState.includes(snapPoint)) return;

        setSnapPointsState([...snapPointsState, snapPoint].sort());
    };

    useEffect(() => {
        setSheetStateAttribute(sheetRef.current, TRANSITION_ATTRIBUTE, true);
        setSheetStateAttribute(sheetRef.current, FULL_HEIGHT_ATTRIBUTE, activeSnapPoint === 1);

        let cancelRunCallback: (() => void) | undefined;

        // Wait one frame after enabling the transform transition, so programmatic snap changes
        // animate instead of jumping straight to their destination.
        const animationFrameId = requestAnimationFrame(() => {
            const newSheetHeight = getSheetHeightAdjustedForClose(activeSnapPoint, topInset);
            const newHeight = setSheetHeight(sheetRef, newSheetHeight);

            if (newHeight == null) {
                setSheetStateAttribute(sheetRef.current, TRANSITION_ATTRIBUTE, false);
                return;
            }

            setContentHeightToMax(sheetProps);
            setSheetHeightVariable(sheetContainerRef, newHeight);

            cancelRunCallback = runOnTransitionEndWithTimeout(sheetRef, () => {
                setContentHeightToVisibleSpace(sheetProps, useViewportResizeForKeyboard);
                setSheetStateAttribute(sheetRef.current, TRANSITION_ATTRIBUTE, false);
            });
        });

        return () => {
            cancelAnimationFrame(animationFrameId);
            cancelRunCallback?.();
        };
    }, [activeSnapPoint, useViewportResizeForKeyboard]);

    useEffect(() => {
        if (!useViewportResizeForKeyboard || activeSnapPoint === 0) return;

        const updateSheetLayoutForViewportResize = () => {
            const newSheetHeight = getSheetHeightAdjustedForClose(activeSnapPoint, topInset);
            const newHeight = setSheetHeight(sheetRef, newSheetHeight);
            if (newHeight == null) return;

            setSheetStateAttribute(sheetRef.current, FULL_HEIGHT_ATTRIBUTE, activeSnapPoint === 1);
            setSheetHeightVariable(sheetContainerRef, newHeight);
            setContentHeightToVisibleSpace(sheetProps, true);
        };

        updateSheetLayoutForViewportResize();

        window.visualViewport?.addEventListener('resize', updateSheetLayoutForViewportResize);

        return () => {
            window.visualViewport?.removeEventListener('resize', updateSheetLayoutForViewportResize);
        };
    }, [activeSnapPoint, useViewportResizeForKeyboard]);

    return { snapPointsState, addSnapPoint };
};

export default useSheetSnapPointState;
